import {
  isPolyllmLocale,
  POLYLLM_DEFAULT_LOCALE,
  PolyllmLocale,
} from './locales';

const TRANSLATION_LOCALE_OVERRIDES: Partial<Record<PolyllmLocale, string>> = {
  es: 'es-419',
  pt: 'pt-BR',
  zh: 'zh-Hans-CN',
};

export const getFirstSegment = (pathname: string): string | null => {
  const seg = pathname.split('/').filter(Boolean)[0];
  return seg ? seg.toLowerCase() : null;
};

/**
 * The non-default locale encoded in the first path segment, or null.
 * `en` is the default and is served without a prefix, so it is never returned
 * here (`isPolyllmLocale` excludes it).
 *   '/fr/chat'  -> 'fr'
 *   '/chat/x'   -> null  (en / no prefix)
 *   '/en/chat'  -> null  (en is unprefixed; a redundant /en is not a locale)
 *   '/ru/chat'  -> null  (unsupported)
 */
export const getPolyllmUrlLocale = (pathname: string): PolyllmLocale | null => {
  const seg = getFirstSegment(pathname);
  return seg && isPolyllmLocale(seg) ? seg : null;
};

// nb/no → 'no', fil/tl → 'tl' (PolyLLM uses ISO 'no'/'tl', not canonical nb/fil).
const NAV_OVERRIDES: Record<string, PolyllmLocale> = {
  nb: 'no',
  no: 'no',
  fil: 'tl',
  tl: 'tl',
};

/**
 * Map an arbitrary BCP-47 / navigator code to a supported PolyLLM locale, or
 * null. `en` (and its regional variants) returns null because en is the
 * default and never an explicit prefix.
 */
export const canonicalizeToPolyllm = (raw?: string): PolyllmLocale | null => {
  if (!raw) {
    return null;
  }
  const lower = raw.toLowerCase();
  const base = lower.split('-')[0];
  if (NAV_OVERRIDES[lower]) {
    return NAV_OVERRIDES[lower];
  }
  if (NAV_OVERRIDES[base]) {
    return NAV_OVERRIDES[base];
  }
  if (isPolyllmLocale(base)) {
    return base;
  }
  return null;
};

/** Resolution order for the bare `/` entry: saved platform lang → browser → en. */
export const resolvePolyllmLocale = (
  savedLang?: string,
  navigatorLang?: string,
): string => {
  return (
    canonicalizeToPolyllm(savedLang) ??
    canonicalizeToPolyllm(navigatorLang) ??
    POLYLLM_DEFAULT_LOCALE
  );
};

export const getPolyllmTranslationLocale = (
  locale: PolyllmLocale | typeof POLYLLM_DEFAULT_LOCALE,
): string => {
  if (locale === POLYLLM_DEFAULT_LOCALE) {
    return POLYLLM_DEFAULT_LOCALE;
  }

  return TRANSLATION_LOCALE_OVERRIDES[locale] ?? locale;
};
